import React, { useEffect, useState, useContext, useCallback } from 'react';
import { Redirect } from 'react-router-dom';
import { Context } from 'components/Store';
import { isEmpty } from 'lodash';
import LandingSearch from 'components/LandingSearch';
import EmptyTableState from 'components/EmptyTableState';
import Paper from 'components/Paper';
import LoadingSpinner from 'components/LoadingSpinner';
import UiKitButton from 'components/UiKit/Button';
import AddRoundedIcon from '@material-ui/icons/AddRounded';
import useLandingPageData from 'hooks/useLandingPageData';
import Breadcrumb from 'components/App/AppShell/components/Breadcrumb';
import NoCompaniesFoundIcon from 'assets/no_applications_found.svg';
import { paths } from 'routes/paths';
import CompanyList from './components/CompanyList';
import CompanyToolbar from './components/CompanyToolbar';

const Companies = () => {
  const [companyPageNumber, setCompanyPageNumber] = useState(0);
  const {
    view,
    setView,
    sort,
    setSort,
    setSortByOverride,
    isLoading,
    setReload,
    setSearchText: setCompanySearchText,
    items: companies,
    totalItems: totalCompanies
  } = useLandingPageData(
    'worker/companies',
    ['company.name', 'company.legalName'],
    'company.name',
    companyPageNumber
  );
  const [{ loggedInUserOrgPermission, loadingOrg }, dispatch] = useContext(
    Context
  );
  const [orgReloading, setOrgReloading] = useState(false);
  const [redirect, setRedirect] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [tableSortBy, setTableSortBy] = useState('');

  useEffect(() => {
    dispatch({
      type: 'SET_TOOLBAR',
      payload: (
        <CompanyToolbar
          view={view}
          changeView={event => setView(event)}
          sort={sort}
          changeSort={event => {
            setSort(event);
            setSortByOverride('name');
            setTableSortBy('');
            setCompanyPageNumber(0);
          }}
        />
      )
    });
    return () => {
      dispatch({ type: 'SET_TOOLBAR', payload: null });
    };
  }, [view, setView, sort, setSort, setSortByOverride, dispatch]);

  useEffect(() => {
    if (isEmpty(loggedInUserOrgPermission)) {
      setOrgReloading(false);
    } else {
      setOrgReloading(true);
    }
  }, [loggedInUserOrgPermission]);

  const getAddButtonLayout = () => {
    if (companies?.length < 1) return 'Create Company';
    return 'Add Company';
  };

  const handleOrgChange = () => {
    setCompanyPageNumber(0);
    setReload(true);
    // setOrgReloading(true);
  };

  const canShowContent = () => {
    return !loadingOrg && orgReloading;
  };

  const setPermission = useCallback(() => {
    if (loggedInUserOrgPermission?.role !== 'View Only') {
      return true;
    }
    return false;
  }, [loggedInUserOrgPermission?.role]);

  const handlePageChange = page => {
    setCompanyPageNumber(page);
  };

  const handleSort = (field, direction) => {
    const sortFields = { name: 'name' };
    if (!sortFields[field]) return;

    setSortByOverride(direction ? sortFields[field] : null);
    setSort(direction || 'asc');
    setTableSortBy(direction ? field : '');
    setCompanyPageNumber(0);
  };

  return (
    <div className="w-full h-full" data-testid="companies">
      <Breadcrumb onOrganizationSelect={handleOrgChange} hideCropSeasonDropdown>
        <Breadcrumb.Item title="Company" value="All Companies" isLast />
      </Breadcrumb>
      {redirect && (
        <Redirect
          push
          to={{
            pathname: `${paths.companies}/company`
          }}
        />
      )}
      <div className="flex justify-between flex-1 mb-3">
        <LandingSearch
          searchValue={searchText}
          name="search_companies"
          id="companies-search-input"
          selectedItems={[{}]}
          showLabels={false}
          showDateRange={false}
          showCropSeason={false}
          onChange={event => {
            const searchTerm = event.target.value;
            setSearchText(searchTerm);
            setCompanyPageNumber(0);
            setCompanySearchText(searchTerm);
          }}
          placeholder="Search Companies"
        />
        <div className="h-8">
          {setPermission() ? (
            <UiKitButton
              id="add-company-button"
              data-testid="add_company"
              variant="primary"
              icon={<AddRoundedIcon />}
              onClick={event => {
                event.stopPropagation();
                setRedirect(true);
              }}
            >
              {getAddButtonLayout()}
            </UiKitButton>
          ) : null}
        </div>
      </div>
      {canShowContent() &&
      !isLoading &&
      companies.length === 0 &&
      searchText.trim() ? (
        <Paper>
          <EmptyTableState
            containerClassName="w-full flex flex-col justify-center items-center py-8"
            title="No Results Found"
            message="Try a different search."
            overlayIcon={NoCompaniesFoundIcon}
          />
        </Paper>
      ) : null}
      {canShowContent() &&
      (isLoading || !(companies.length === 0 && searchText.trim())) ? (
        <CompanyList
          companies={companies}
          isLoading={isLoading}
          memberRole={loggedInUserOrgPermission?.role}
          totalCompanies={totalCompanies}
          companyPageNumber={companyPageNumber}
          handlePageChange={handlePageChange}
          sortBy={tableSortBy}
          sortDir={sort}
          onSort={handleSort}
        />
      ) : null}
      {!canShowContent() && (
        <div
          className="flex items-center justify-center w-full h-full"
          data-testid="companies-loading"
        >
          <LoadingSpinner message="" size={50} label="Loading companies" />
        </div>
      )}
    </div>
  );
};
export default Companies;
